import React, { createContext, useContext, useMemo, useState } from 'react';
import { Drawer, Props as DrawerProps } from '@/components/drawer';
import { LayoutSize, useLayoutContext } from './layout-context';

export type CloseCallback = () => void;

export interface DrawerContextType<T> {
  closeDrawer: () => void;
  open: boolean;
  openDrawer: () => void;
  setCloseable: (closeable: boolean) => void;
  setCloseCallback: (callback: CloseCallback) => void;
  setDrawerState: (nextValue: T) => void;
}

// exclude injected props
type DrawerContextDrawerProps<T> = Omit<
  DrawerProps<T>,
  'onClose' | 'open' | 'extendedState' | 'children'
>;

interface Props {
  children?: React.ReactNode;
}

export const createDrawerContext = <T extends {}>(
  drawerProps:
    | DrawerContextDrawerProps<T>
    | ((layoutSize: LayoutSize) => DrawerContextDrawerProps<T>),
) => {
  const DrawerContext = createContext<DrawerContextType<T> | null>(null);

  const DrawerContextProvider: React.FC<Props> = ({ children }) => {
    const { layoutSize } = useLayoutContext();
    const [closeable, setCloseable] = useState(true);
    const [closeCallback, setCloseCallback] = useState<CloseCallback | null>(
      null,
    );
    const [drawerState, setDrawerState] = useState<T>();

    const [open, setOpen] = useState(false);
    const contextValue = useMemo<DrawerContextType<T>>(() => {
      return {
        closeDrawer: () => {
          if (!closeable) {
            return;
          }

          setOpen(false);

          // Call and reset the close callback
          closeCallback?.();
          setCloseCallback(null);
        },
        open,
        openDrawer: () => setOpen(true),
        setCloseable,
        setCloseCallback: setCloseCallback,
        setDrawerState,
      };
    }, [closeable, closeCallback, open]);

    return (
      <DrawerContext.Provider value={contextValue}>
        <Drawer<T>
          {...(typeof drawerProps === 'function'
            ? drawerProps(layoutSize)
            : drawerProps)}
          extendedState={drawerState}
          onClose={contextValue.closeDrawer}
          open={open}
        >
          {children}
        </Drawer>
      </DrawerContext.Provider>
    );
  };

  return {
    DrawerContext,
    DrawerContextProvider,
    useDrawerContext: () => {
      const context = useContext(DrawerContext);
      if (!context) {
        throw new Error('useDrawerContext requires a DrawerContextProvider');
      }

      return context;
    },
    useMaybeDrawerContext: () => useContext(DrawerContext),
  };
};
